<script setup>
import { onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { provideLoading } from '@/composables/useLoading';
import AppLoader from '@/components/AppLoader.vue';
import UpdateNotification from '@/components/UpdateNotification.vue';
import ConfirmDialog from 'primevue/confirmdialog';
import { versionCheckService } from '@/service/versionCheck';

provideLoading();

const route = useRoute();

// Only initialize version checking on authenticated (non-landing) routes
watch(
    () => route.path,
    (path) => {
        if (path && !path.startsWith('/landing')) {
            versionCheckService.initialize();
        }
    },
    { immediate: true }
);

onUnmounted(() => {
    versionCheckService.destroy();
});
</script>

<template>
    <AppLoader />
    <UpdateNotification />
    <router-view />
    <ConfirmDialog />
</template>

<style>
/*
 * Legacy PrimeFlex-style layout helpers. Two layers of protection against
 * collisions with Tailwind v4 utilities:
 *  1. `:has(> [class*='col-'])` scopes the helpers to grids that wrap
 *     PrimeFlex-style col-* children, narrowing their reach.
 *  2. `@layer base` ensures that even when the selector still matches
 *     (e.g. Tailwind `col-span-*` children also match `[class*='col-']`),
 *     Tailwind utilities in `@layer utilities` win the cascade.
 */
@layer base {
    .grid:has(> [class*='col-']) {
        min-height: calc(100vh - 75px);
        display: flex;
    }

    .grid:has(> [class*='col-']) > [class*='col-'] {
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    /* Make tab view fill available space */
    :deep(.p-tabview) {
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    :deep(.p-tabview-panels) {
        flex: 1;
        overflow: auto;
    }

    :deep(.p-tabview-panel) {
        height: 100%;
    }

    /* Make card fill the tab panel */
    :deep(.p-tabview-panel) .p-card {
        height: 100%;
    }

    /* Adjust PDF container */
    .pdf-container {
        height: 100%;
        min-height: 600px;
    }
}
</style>
